// Types for the RAG API

export type LexicalSearchMethod = 'bm25s' | 'bm25plus' | 'bm25okapi' | 'bm25l';
export enum LangCode {
  EN_GB = 'en-GB',
  ES_ES = 'es-ES',
  PT_PT = 'pt-PT',
}
export enum ProjectRole {
  ADMIN = 'ADMIN',
  EDIT = 'EDIT',
  VIEW = 'VIEW',
}

export enum ProjectStatus {
  NEEDS_INDEXING = 'NEEDS_INDEXING',
  INDEXING = 'INDEXING',
  INDEXING_ERROR = 'INDEXING_ERROR',
  READY_FOR_ANSWERING = 'READY_FOR_ANSWERING',
  ANSWERING = 'ANSWERING',
  ANSWERING_ERROR = 'ANSWERING_ERROR',
  READY_FOR_REPORT = 'READY_FOR_REPORT',
  REPORT_ERROR = 'REPORT_ERROR',
  CANCELLING = 'CANCELLING'
}

export enum QuestionnaireStatus {
  NEEDS_PROCESSING = 'NEEDS_PROCESSING',
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  PROCESSING_ERROR = 'PROCESSING_ERROR',
  /** Execução interrompida a meio, com parte das perguntas respondidas e
   * parte por responder. Retomável com Play (o backend reaproveita as
   * respostas já geradas) — distinto de NEEDS_PROCESSING, que significa
   * "ainda não produziu nenhuma resposta". */
  PARTIALLY_PROCESSED = 'PARTIALLY_PROCESSED'
}

export const PROJECT_STATUS_DOT: Record<ProjectStatus, string> = {
  [ProjectStatus.NEEDS_INDEXING]: 'bg-yellow-500',
  [ProjectStatus.INDEXING]: 'bg-yellow-500',
  [ProjectStatus.INDEXING_ERROR]: 'bg-red-500',
  [ProjectStatus.READY_FOR_ANSWERING]: 'bg-green-500',
  [ProjectStatus.ANSWERING]: 'bg-green-500',
  [ProjectStatus.ANSWERING_ERROR]: 'bg-red-500',
  [ProjectStatus.READY_FOR_REPORT]: 'bg-blue-500',
  [ProjectStatus.REPORT_ERROR]: 'bg-red-500',
  [ProjectStatus.CANCELLING]: 'bg-orange-500',
};

export const PROJECT_STATUS_TEXT: Record<ProjectStatus, string> = {
  [ProjectStatus.NEEDS_INDEXING]: 'text-yellow-700',
  [ProjectStatus.INDEXING]: 'text-yellow-700',
  [ProjectStatus.INDEXING_ERROR]: 'text-red-600',
  [ProjectStatus.READY_FOR_ANSWERING]: 'text-green-700',
  [ProjectStatus.ANSWERING]: 'text-green-700',
  [ProjectStatus.ANSWERING_ERROR]: 'text-red-600',
  [ProjectStatus.READY_FOR_REPORT]: 'text-blue-700',
  [ProjectStatus.REPORT_ERROR]: 'text-red-600',
  [ProjectStatus.CANCELLING]: 'text-orange-700',
};

export const ALL_PROJECT_STATUSES: ProjectStatus[] = [
  ProjectStatus.NEEDS_INDEXING,
  ProjectStatus.INDEXING,
  ProjectStatus.INDEXING_ERROR,
  ProjectStatus.READY_FOR_ANSWERING,
  ProjectStatus.ANSWERING,
  ProjectStatus.ANSWERING_ERROR,
  ProjectStatus.READY_FOR_REPORT,
  ProjectStatus.REPORT_ERROR,
  ProjectStatus.CANCELLING,
];

export function resolveProjectStatus(
  status: ProjectStatus | null | undefined,
  isActive: boolean,
): ProjectStatus {
  return status ?? (isActive ? ProjectStatus.READY_FOR_ANSWERING : ProjectStatus.NEEDS_INDEXING);
}

export interface SessionConfig {
  source_folder: string;
  destination_folder: string;
  modelo_llm: string;
  modelo_embeddings: string;
  temperature: number;
  chunk_size: number;
  chunk_overlap: number;
  k_retrieval: number;
  bm25_weight: number;
  lexical_search_method: LexicalSearchMethod;
}

export interface IndexingStatus {
  status: 'idle' | 'uploading' | 'scanning' | 'extracting' | 'embedding' | 'indexing' | 'cancelling' | 'completed' | 'error';
  total_documents: number;
  processed_documents: number;
  current_document: string | null;
  progress_percent: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface Session {
  session_id: string;
  id_token: string;
  created_at: string;
  config: SessionConfig;
  indexing_status: IndexingStatus;
  questions_count: number;
  indexed_documents: number;
}

export interface OllamaModel {
  name: string;
  size: number;
  modified_at: string | null;
  digest: string | null;
}

export interface ConfidenceMetrics {
  confidence: number;
  level: 'Alta' | 'Média' | 'Baixa' | 'N/A';
  doc_consistency: number;
  source_diversity: number;
  length_score: number;
  directness: number;
}

export interface QueryResponse {
  question: string;
  answer: string;
  confidence: ConfidenceMetrics;
  sources: string[];
  chunks_analyzed: number;
  query_time_ms: number;
  cached: boolean;
  timestamp: string;
}

export interface WSMessage {
  type: string;
  session_id: string;
  project_id?: string;
  timestamp: string;
  data: Record<string, any>;
}

export type TestParameterValue = string | number | boolean;

export interface TestParameter {
  name: string;
  current_value: TestParameterValue;
  range: TestParameterValue[];
  section: string;
}

export interface User {
  user_id: string;
  name: string;
  email: string;
  role_id: ProjectRole | string | null;
  created_at: string;
}

export interface UsersListResponse {
  items: User[];
  total: number;
}

export interface Project {
  project_id: string;
  name: string;
  created_at: string;
  created_by?: User | null;
  members: User[];
  is_active: boolean;
  status?: ProjectStatus | null;
  role?: ProjectRole | null;
  files_count: number;
  /** Estado da barra de indexação tal como o servidor o tem persistido. É a
   * fonte de verdade do progresso: o WebSocket só antecipa o que isto já
   * diria (ver `syncProjectFromServer` no indexingStore). */
  indexing_progress_percent?: number;
  indexing_progress_stage?: string | null;
  /** Código de mensagem traduzível (+ `params`), o mesmo que vem nas
   * mensagens de progresso por WebSocket. */
  indexing_progress_code?: string | null;
  indexing_progress_params?: Record<string, unknown> | null;
}

export interface ProjectsListResponse {
  items: Project[];
  total: number;
}

export type ProjectSortByField = 'name' | 'created_by' | 'files_count' | 'status' | 'created_at';
export type SortOrder = 'asc' | 'desc';

export interface QuestionItem {
  question_id: string;
  order: number;
  text: string;
}

export interface Questionnaire {
  questionnaire_id: string;
  project_id: string;
  name: string;
  created_at: string;
  questions_count?: number;
  /** Perguntas já com resposta persistida — usado para mostrar "X/Y" num
   * questionário PARTIALLY_PROCESSED. */
  answered_questions_count?: number;
  project_name?: string;
  status: QuestionnaireStatus;
  questions?: QuestionItem[];
  min_generation_time_ms?: number;
  max_generation_time_ms?: number;
  /** Original (criado de raiz, ou cópia ainda intacta) — false assim que é
   * copiado para outro projeto ou as suas perguntas são editadas. */
  is_original?: boolean;
}

export interface QuestionnairesListResponse {
  items: Questionnaire[];
  total: number;
}

export interface Question {
  question_id: string;
  questionnaire_id: string;
  text: string;
  created_at: string;
}

export interface QuestionsListResponse {
  items: Question[];
  total: number;
}

export interface QuestionAnswerFileSource {
  name: string;
  pages: number[];
}

export interface QuestionAnswerItem {
  name: string;
  question_id: string;
  question_text: string;
  order: number;
  answer_text: string | null;
  level: string | null;
  score: number | null;
  answer_created_at: string | null;
  files: Record<string, QuestionAnswerFileSource>;
  confidence_task_status: string | null;
}

export interface QuestionnaireAnswersListResponse {
  items: QuestionAnswerItem[];
  total: number;
}

export interface ProjectFile {
  file_id: string;
  project_id: string;
  file_name: string;
  uploaded_at: string;
  /** null enquanto a indexação atómica deste ficheiro (disparada pelo upload) ainda não terminou. */
  indexed_at: string | null;
  /** A última tentativa de processamento deste ficheiro falhou em definitivo
   * (esgotou as tentativas). Um ficheiro falhado tem `indexed_at` a null para
   * sempre — sem esta flag não havia como distingui-lo de um que ainda está a
   * processar, e a lista mostrava o loader eternamente. */
  indexing_failed: boolean;
}

export interface FilesListResponse {
  items: ProjectFile[];
  total: number;
}

export interface FileUploadError {
  file_name: string;
  code: string;
  params: Record<string, unknown>;
}

export interface FilesUploadResponse {
  items: ProjectFile[];
  errors: FileUploadError[];
}
